import { applyAction } from './apply';
import { REPLAY_FORMAT_VERSION, RULES_VERSION } from './config';
import { startMatch } from './setup';
import type { Action, MatchConfig, MatchEvent, MatchState, Replay } from './types';

/** A replay record (§16.5). */
export const makeReplay = (
  config: MatchConfig,
  seed: string | number,
  actions: readonly Action[],
): Replay => ({
  rulesVersion: RULES_VERSION,
  replayFormat: REPLAY_FORMAT_VERSION,
  config,
  seed,
  actions: actions.slice(),
});

export interface ReplayRun {
  readonly state: MatchState;
  /** Events of match setup, then one list per applied action. */
  readonly setupEvents: MatchEvent[];
  readonly actionEvents: MatchEvent[][];
}

export class ReplayError extends Error {
  constructor(
    message: string,
    readonly index: number,
  ) {
    super(message);
    this.name = 'ReplayError';
  }
}

/** The replay format of a record; files written before the field existed are format 1. */
export const replayFormatOf = (replay: Replay): number => replay.replayFormat ?? 1;

/**
 * Replays a match from { rulesVersion, config, seed, actions } (§16.5). Throws a ReplayError if the
 * rules version or replay format differs or an action is rejected. `upTo` limits how many actions
 * are applied.
 */
export function runReplay(
  replay: Replay,
  upTo = replay.actions.length,
  onStep?: (before: MatchState, action: Action, after: MatchState, events: MatchEvent[]) => void,
): ReplayRun {
  if (replay.rulesVersion !== RULES_VERSION)
    throw new ReplayError(
      `replay uses rules ${replay.rulesVersion}; this engine implements ${RULES_VERSION}`,
      -1,
    );
  if (replayFormatOf(replay) !== REPLAY_FORMAT_VERSION)
    throw new ReplayError(
      `replay format ${replayFormatOf(replay)} is not supported; this engine reads format ${REPLAY_FORMAT_VERSION}`,
      -1,
    );
  const start = startMatch(replay.config, replay.seed);
  let state = start.state;
  const actionEvents: MatchEvent[][] = [];
  const n = Math.min(upTo, replay.actions.length);
  for (let i = 0; i < n; i++) {
    const res = applyAction(state, replay.actions[i] as Action);
    if (res.error)
      throw new ReplayError(`action ${i} rejected: ${res.error.code} ${res.error.message}`, i);
    onStep?.(state, replay.actions[i] as Action, res.state, res.events);
    state = res.state;
    actionEvents.push(res.events);
  }
  return { state, setupEvents: start.events, actionEvents };
}

/** Validates the outer shape of an untrusted replay object (e.g. an imported file). */
export function isReplayShape(v: unknown): v is Replay {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Partial<Replay>;
  return (
    typeof r.rulesVersion === 'string' &&
    typeof r.config === 'object' &&
    r.config !== null &&
    (typeof r.seed === 'string' || typeof r.seed === 'number') &&
    (r.replayFormat === undefined || Number.isInteger(r.replayFormat)) &&
    Array.isArray(r.actions)
  );
}
